import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import UUID from 'uuid-js';
import sortBy from 'lodash/sortBy';

import SVGIcon from './svg-icon.js';

import { cmsColors } from '../../edlio-js/constants/index'

const KEYS_MAP = {
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ENTER: 'Enter',
  SPACE: ' ',
  ESC: 'Escape'
}

/**
 * SelectMenu provides a standardized template for displaying a 'select' dropdown element alternative. Clicking on
 *   the element reveals the menu element containing SelectMenu's options. Options are provided to the SelectMenu
 *   element through the options prop
 *
 * Uniform Dependencies
 * @requires css:components/select-menus
 */
const SelectMenu = (props) => {

  const { 
    block, 
    width, 
    disabled, 
    hasError, 
    iconColor, 
    maxWidth,
    ellipsis,
    iconOnly,
    dataQA,
    title,
    margin,
    padding,
    options,
    selectedOption,
    direction,
    iconMargin,
    additionalClasses,
    linkColor,
    iconName,
    sort,
    lineHeight,
    svgViewBox,
    addBodyClickHandlers,
    flex,
    iconPosition,
    focusOnOpen,
    menuMaxHeight,
    menuDisplayCallback
  } = props;

  const [open, setOpen] = useState(false);
  const container = useRef();
  const firstOptionRef = useRef(null);
  const uniqueId = UUID.create();

  useEffect(() => {
    if (addBodyClickHandlers) {
      document.body.addEventListener('mousedown', handleDocumentClick, false);
      document.body.addEventListener('touchstart', handleDocumentClick, false);
    }

    return () => {
      if (addBodyClickHandlers) {
        document.body.removeEventListener('mousedown', handleDocumentClick, false);
        document.body.removeEventListener('touchstart', handleDocumentClick, false);
      }
    }
  }, [addBodyClickHandlers, open])

  useEffect(() => {
    if (open && focusOnOpen && firstOptionRef.current) firstOptionRef.current.focus();
  }, [open])

  const handleDocumentClick = (evt) => {
    if (evt.target.nodeName === 'A' && evt.target.className == 'select-menu-link') {
      evt.preventDefault();
    }

    if (open && !container.current.contains(evt.target)) {
      toggleMenuDisplay(false)
    }
  }

  const getWidth = () => {
    if (block) {
      return '100%';
    } else if (width) {
      return width;
    } else {
      return null;
    }
  }

  const getColor = () => {
    return disabled ? cmsColors.grey : null;
  }

  const getBorder = () => {
    if (disabled) {
      return `1px solid ${cmsColors.grey}`;
    } else if (hasError) {
      return `1px solid ${cmsColors.alertColor}`;
    } else {
      return '1px solid #444444';
    }
  }

  const getIconColor = () => {
    if (iconColor) {
      return iconColor;
    } else if (disabled) {
      return cmsColors.grey;
    } else {
      return null;
    }
  }

  const getHeaderStyles = () => {
    return {
      display: (block || flex) ? 'flex' : null,
      justifyContent: (block || flex) ? 'space-between' : null,
      alignItems: (block || flex) ? 'center' : null,
      padding: (block || flex) ? '0 1em' : null,
      flexDirection: iconPosition === 'left' ? 'row-reverse' : 'row',
      maxWidth
    };
  }

  const getHeaderTitleStyles = () => {
    return ellipsis ? {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    } : {};
  }

  const toggleMenuDisplay = (value) => {
    setOpen(value)

    if (typeof menuDisplayCallback === 'function') {
      menuDisplayCallback(value);
    }
  }

  const openMenu = () => {
    toggleMenuDisplay(true)
  }

  const keyActions = {
    [KEYS_MAP.ENTER]: (evntTarget) => openMenu(evntTarget),
    [KEYS_MAP.SPACE]: (evntTarget) => openMenu(evntTarget),
    [KEYS_MAP.ARROW_DOWN]: (evntTarget) => openMenu(evntTarget),
    [KEYS_MAP.ARROW_UP]: () => {
      toggleMenuDisplay(false)
    }
  }

  const handleKeyDown = (event) => {
    const action = keyActions[event.key];
    if (action) action(event.target);
  }

  const displayTitle = selectedOption || title;

  const classNames = [
    open && 'open',
    'edlio-select',
    'uniform-select',
    iconOnly && 'menu',
    additionalClasses
  ].filter(c => c).join(' ');

  const headerClassNames = [
    (!block && !flex) && 'select-title',
    iconOnly && 'menu'
  ].filter(c => c).join(' ');
  
  const background = hasError && '#faece2';

  const sortedOptions = sort ? sortBy(options, ['label']) : options;

  const firstSelectableOptionIndex = sortedOptions.findIndex((option) => option.display !== 'none');

  return (
    <div
      ref={container}
      className={classNames}
      data-qa={dataQA}
      style={{
        margin,
        padding,
        background,
        color: getColor(),
        border: getBorder(),
        width: getWidth(),
        pointerEvents: disabled ? 'none' : null,
        lineHeight
      }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <header
        className={headerClassNames}
        style={getHeaderStyles()}
        onClick={() => toggleMenuDisplay(true)}
      >
        {displayTitle && <span style={getHeaderTitleStyles()}>{displayTitle}</span>}
        <SVGIcon
          {...(!displayTitle && { margin: '0' })}
          viewBox={svgViewBox}
          name={iconName}
          color={getIconColor()}
        />
      </header>
      {
        open &&
        <menu style={{ zIndex: 3, maxHeight: menuMaxHeight }} className={direction}>
          { sortedOptions.map((option, index) => {
            return (
              <SelectOption
                key={`${option.key || option.label}-${uniqueId}-${index}`}
                display={option.display}
                label={option.label}
                badge={option.badge}
                iconName={option.iconName}
                color={linkColor}
                iconMargin={option.iconMargin || iconMargin}
                href={option.href}
                target={option.target}
                callback={option.callback}
                onOptionSelect={() => toggleMenuDisplay(false)}
                selected={(option.label === selectedOption) || (option.selected)}
                disabled={option.disabled}
                dataQA={option.dataQA}
                borderTop={option.borderTop}
                ellipsis={ellipsis}
                maxWidth={maxWidth}
                virtualRef={index === firstSelectableOptionIndex ? firstOptionRef : undefined}
                fontWeight={option.fontWeight}
              />
            )
          })}
        </menu>
      }
    </div>
  )
}

SelectMenu.propTypes = {
  title: PropTypes.node,
  width: PropTypes.string,
  margin: PropTypes.string,
  padding: PropTypes.string,
  // further defined in SelectOption propTypes
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedOption: PropTypes.string,
  direction: PropTypes.oneOf(['left', 'right']),
  iconOnly: PropTypes.bool,
  iconMargin: PropTypes.string,
  iconColor: PropTypes.string,
  linkColor: PropTypes.string,
  iconName: PropTypes.string,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  lineHeight: PropTypes.string,
  block: PropTypes.bool,
  flex: PropTypes.bool,
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
  dataQA: PropTypes.string,
  sort: PropTypes.bool,
  ellipsis: PropTypes.bool,
  maxWidth: PropTypes.string,
  svgViewBox: PropTypes.string,
  focusOnOpen: PropTypes.bool,
  menuMaxHeight: PropTypes.string,
  menuDisplayCallback: PropTypes.func,

  /**
   * @deprecated
   */
  additionalClasses: PropTypes.string,
  addBodyClickHandlers: PropTypes.bool
};

SelectMenu.defaultProps = {
  iconName: 'caret-down',
  direction: 'left',
  addBodyClickHandlers: true,
  block: false,
  flex: false,
  svgViewBox: '0 0 2048 2048',
  sort: false,
  additionalClasses: '',
  iconPosition: 'right',
  focusOnOpen: true
};

export default SelectMenu;

const SelectOption = (props) => {

  const {
    href,
    display,
    iconMargin,
    color,
    iconName,
    badge,
    label,
    selected,
    disabled,
    target,
    dataQA,
    virtualRef,
    onOptionSelect,
    callback,
    maxWidth,
    borderTop,
    ellipsis,
    fontWeight
  } = props;

  const linkRef = useRef(null);

  const handleClick = () => {

    if (disabled) return;

    onOptionSelect();

    if (callback) {
      callback()
    }
  }

  const getKeyActions = () => {

    return {
      [KEYS_MAP.ENTER]() {
        if (href) {
          linkRef.current.click();
        } else {
          handleClick()
        }
      },
      [KEYS_MAP.ARROW_DOWN](evntTarget) {
        if (evntTarget.nextSibling) evntTarget.nextSibling.focus();
      },
      [KEYS_MAP.ARROW_UP](evntTarget) {
        if (evntTarget.previousSibling) evntTarget.previousSibling.focus();
      },
      [KEYS_MAP.ESC](evntTarget) {
        onOptionSelect();
        evntTarget.parentNode.previousSibling.focus();
      }
    }
  }

  const handleKeyDown = (event) => {
    event.stopPropagation();
    const action = getKeyActions()[event.key];
    if (action) action(event.target);
  }

  const handleBlur = (event) => {
    if (event.relatedTarget && !event.relatedTarget.classList.contains('item')) onOptionSelect()
  }

  const getOptionStyles = () => {
    const styles = {
      display,
      cursor: disabled ? 'default' : 'pointer',
      maxWidth,
      borderTop: borderTop ? `1px solid ${cmsColors.grey}` : ''
    }

    if (ellipsis) {
      styles.textOverflow = 'ellipsis'
      styles.whiteSpace = 'nowrap'
      styles.overflow = 'hidden'
    }

    return styles
  }

  const classNames = [
    'item',
    selected && 'selected',
    href && 'with-link'
  ].filter(c => c).join(' ');

  return (
    <div
      className={classNames}
      style={getOptionStyles()}
      data-qa={dataQA}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      onBlur={handleBlur}
      ref={virtualRef}
    >
      {href ?
        <a href={href} style={{ display, color }} target={target} ref={linkRef} className='select-menu-link'>
          {iconName &&
            <SVGIcon 
              viewBox={iconName === 'pencil' ? '-200 0 2048 2048' : null} 
              margin={iconMargin} 
              name={iconName}
            />
          }
          {label}
        </a>
        :
        <span style={{ color: disabled ? cmsColors.grey : color, fontWeight }}>
          {iconName &&
            <SVGIcon
              viewBox={iconName === 'pencil' ? '-200 0 2048 2048' : null} 
              margin={iconMargin}
              name={iconName}
            />
          }

          {label}

          {badge && <uniform-badge>{badge}</uniform-badge>}

          {selected &&
            <SVGIcon name='check' />
          }
        </span>
      }
    </div>
  )
}

SelectOption.propTypes = {
  display: PropTypes.string,
  label: PropTypes.string.isRequired,
  iconName: PropTypes.string,
  badge: PropTypes.string,
  color: PropTypes.string,
  iconMargin: PropTypes.string,
  href: PropTypes.string,
  target: PropTypes.string,
  callback: PropTypes.func,
  borderTop: PropTypes.bool,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  onOptionSelect: PropTypes.func.isRequired,
  ellipsis: PropTypes.bool,
  maxWidth: PropTypes.string,
  dataQA: PropTypes.string,
  virtualRef: PropTypes.any,
  fontWeight: PropTypes.string
};

SelectOption.defaultProps = {
  display: 'block',
  selected: false,
  disabled: false,
  target: '_self'
};



// WEBPACK FOOTER //
// ./src/main/webapps/common/react/components/select-menu.js